import { EventType, SafeAny } from '@/common/types';
import { ScoreSetEvent } from '@/modules/epoch/types';
import { formatTimeValue, i18n } from '@/common/utils';
import { SignalEvent } from '@/modules/epoch/data-access';
import { utcFormat } from 'd3';

type NewEvent = { start: string; duration: number; id: string | undefined };

type Events = { start: string; duration: number; id: string }[] | undefined;

function getEventRange(start: string, duration: number) {
  const startTime = new Date(start).getTime();
  const endTime = startTime + duration * 1000;

  return { startTime, endTime };
}

function isOverlappingRange(startA: number, endA: number, startB: number, endB: number) {
  return startA < endB && endA > startB;
}

export function isOverlappingEvent(
  newEvent: NewEvent,
  events: Events,
  eventIdMap?: Record<string, string>
): boolean {
  if (!events?.length) return false;

  const hasSameEvent = events.some(
    (event) => event.start === newEvent.start && event.duration === newEvent.duration
  );

  if (hasSameEvent) return false;

  const { startTime: newStart, endTime: newEnd } = getEventRange(newEvent.start, newEvent.duration);

  const resolvedTargetId = newEvent.id ? eventIdMap?.[newEvent.id] ?? newEvent.id : null;

  const otherEvents = resolvedTargetId ? events.filter((e) => e.id !== newEvent.id) : events;

  return otherEvents.some((event) => {
    const { startTime, endTime } = getEventRange(event.start, event.duration);

    return isOverlappingRange(newStart, newEnd, startTime, endTime);
  });
}

export function getOverlappingEventsWithIDs(
  newEvent: NewEvent,
  events: Events,
  eventIdMap?: Record<string, string>
): any[] {
  if (!events?.length) return [];

  const { startTime: newStart, endTime: newEnd } = getEventRange(newEvent.start, newEvent.duration);

  const resolvedTargetId = newEvent.id ? eventIdMap?.[newEvent.id] ?? newEvent.id : null;

  const otherEvents = resolvedTargetId
    ? events.filter((e) => {
        const resolvedEId = e.id ? eventIdMap?.[e.id] ?? e.id : e.id;
        return resolvedEId !== resolvedTargetId;
      })
    : events;

  return otherEvents.filter((event) => {
    const { startTime, endTime } = getEventRange(event.start, event.duration);
    return isOverlappingRange(newStart, newEnd, startTime, endTime);
  });
}

const RESPIRATORY_EVENTS = new Set<EventType>([
  EventType.APNEA_CENTRAL,
  EventType.APNEA_CENTRAL_RESP_FLOW,
  EventType.APNEA_MIXED,
  EventType.APNEA_MIXED_RESP_FLOW,
  EventType.APNEA_OBSTRUCTIVE,
  EventType.APNEA_OBSTRUCTIVE_RESP_FLOW,
  EventType.CHEYNE_STOKES_BREATHING,
  EventType.SNORING,
  EventType.HYPOPNEA_CENTRAL,
  EventType.HYPOPNEA_CENTRAL_RESP_FLOW,
  EventType.HYPOPNEA_OBSTRUCTIVE,
  EventType.HYPOPNEA_OBSTRUCTIVE_RESP_FLOW,
  EventType.RERA,
  EventType.RERA_RESP_FLOW
]);

const ANY_SLEEP_OVERLAP_EVENTS = new Set<EventType>([
  EventType.BRUXISM_TONIC_L,
  EventType.BRUXISM_TONIC_R,
  EventType.BRUXISM_MIX_L,
  EventType.BRUXISM_MIX_R,
  EventType.BRUXISM_PHASIC_L,
  EventType.BRUXISM_PHASIC_R,
  EventType.TACHYCARDIA,
  EventType.BRADYCARDIA,
  ...RESPIRATORY_EVENTS
]);

export const isRespiratoryEvent = (type: EventType) => RESPIRATORY_EVENTS.has(type);
export const isSleepOverlapRequiredEvent = (type: EventType) => ANY_SLEEP_OVERLAP_EVENTS.has(type);

/**
 * Block user if wake stage has respiratory events
 * @param startTime
 * @param duration
 * @param events
 * @returns
 */
export const overlappingRespiratoryEventIDs = (
  eventID: string,
  startDate: string,
  duration: number,
  events
): ScoreSetEvent[] => {
  const respiratoryEvents: { [key: string]: any[] } = {};

  for (const [eventType, eventArray] of Object.entries(events)) {
    if (isRespiratoryEvent(eventType as EventType)) {
      respiratoryEvents[eventType] = eventArray as ScoreSetEvent[];
    }
  }

  let overlappingEventIDList = [] as ScoreSetEvent[];

  for (const eventType in respiratoryEvents) {
    if (respiratoryEvents[eventType]) {
      const eventArray = events[eventType];

      const overlappingEventIDs = getOverlappingEventsWithIDs(
        { start: startDate, duration: duration, id: eventID },
        eventArray as any
      );

      overlappingEventIDList = [
        ...overlappingEventIDs,
        ...overlappingEventIDList
      ] as ScoreSetEvent[];
    }
  }

  return overlappingEventIDList;
};

export const buildEventTooltipContent = (activeEvent: SignalEvent) => {
  const { t } = i18n.global;

  return `<p><span class='text-capitalize'>${t('eventStart')}</span>: <strong>${utcFormat(
    '%H:%M:%S'
  )(new Date(activeEvent.start))}</strong></p>
          <p><span class='text-capitalize'>${t('duration')}</span>: <strong>${formatTimeValue(
            activeEvent.duration
          )} ${t('seconds')}</strong></p>
          <p><span class='text-capitalize'>${t(
            'eventType'
          )}</span>: <strong>${getTranslatedValueWithArtifact(activeEvent.eventType)}</strong></p>`;
};

export const findSignalByEventType = (type: SafeAny, signalEventMapping: SafeAny) => {
  for (const signal in signalEventMapping) {
    if (Object.prototype.hasOwnProperty.call(signalEventMapping, signal)) {
      if (signalEventMapping[signal].includes(type)) {
        return signal;
      }
    }
  }
  return null;
};

export const getClonedEventIDbySignal = (
  originalEvent: ScoreSetEvent,
  state: SafeAny,
  signal: string
) => {
  const { duration, start } = originalEvent;
  const signalEvents = state.signalEvents[signal || ''];
  const startTime = new Date(start).getTime();
  return signalEvents
    .filter((event) => event.id !== originalEvent.id)
    .find((event) => event.duration === duration && new Date(event.start).getTime() === startTime);
};

/**
 * Translates a given key, ensuring "Artifact" is translated separately when present in the string.
 * Useful for cases where the key contains a mix of translatable and non-translatable parts.
 * @param value
 * @returns
 */
export const getTranslatedValueWithArtifact = (value: string): string => {
  const { t } = i18n.global;

  return value?.includes('Artifact') ? value.replace('Artifact', t('artifact')) : t?.(value);
};

const eventStartCache = new WeakMap<object, number>();

function getEventStartMs(event: SignalEvent): number {
  let startTime = eventStartCache.get(event);
  if (startTime === undefined) {
    startTime = Date.parse(event.start);
    eventStartCache.set(event, startTime);
  }
  return startTime;
}

export function changeWakeUnstagedRespEvents(
  events: SignalEvent[],
  sleepStagesEvents: SignalEvent[]
): SignalEvent[] {
  if (!events?.length) return events ?? [];

  const intervals: [number, number][] = [];

  if (sleepStagesEvents?.length) {
    for (const stage of sleepStagesEvents) {
      if (stage.eventType !== 'Wake' && stage.eventType !== 'Unstaged') continue;

      const startTime = getEventStartMs(stage);
      const endTime = startTime + stage.duration * 1000;
      intervals.push([startTime, endTime]);
    }
  }

  if (!intervals.length) {
    for (const event of events) event.isRespInWakeUnstaged = false;
    return events;
  }

  intervals.sort((a, b) => a[0] - b[0]);

  const mergedIntervals: [number, number][] = [];
  let [currentStart, currentEnd] = intervals[0];

  for (let i = 1; i < intervals.length; i++) {
    const [nextStart, nextEnd] = intervals[i];
    if (nextStart <= currentEnd) {
      if (nextEnd > currentEnd) currentEnd = nextEnd;
    } else {
      mergedIntervals.push([currentStart, currentEnd]);
      [currentStart, currentEnd] = [nextStart, nextEnd];
    }
  }
  mergedIntervals.push([currentStart, currentEnd]);

  for (const event of events) {
    if (
      !isRespiratoryEvent(event.eventType as EventType) &&
      !isSleepOverlapRequiredEvent(event.eventType as EventType)
    ) {
      event.isRespInWakeUnstaged = false;
      continue;
    }

    const eventStart = getEventStartMs(event);
    const eventEnd = eventStart + event.duration * 1000;

    let low = 0;
    let high = mergedIntervals.length;

    while (low < high) {
      const mid = (low + high) >> 1;
      if (mergedIntervals[mid][1] <= eventStart) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    const isEntirelyInWake =
      low < mergedIntervals.length &&
      mergedIntervals[low][0] <= eventStart &&
      mergedIntervals[low][1] >= eventEnd;

    event.isRespInWakeUnstaged = isEntirelyInWake;
  }

  return events;
}
